/* eslint-disable */
import {useCallback, useMemo, useRef, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, {Circle, Path} from 'react-native-svg';
import {useTranslation} from 'react-i18next';
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import AuthHeader from '../../components/AuthHeader';
import {getMyScans} from '../../services/scan-service';
import {getRewards} from '../../services/rewards-service';
import {colors, card, fonts, radii, spacing, typography} from '../../config/theme';

// How the backend's scan (GET scans/mine) becomes a card.
// Backend status: draft -> pending -> approved / rejected. A draft was never
// sent for review, so it is not listed.
const STATUS_FROM_API = {
  pending: 'Pending',
  submitted: 'Pending', // scans/mine?status=submitted in the API collection
  approved: 'Approved',
  rejected: 'Rejected',
};

const formatDate = iso => {
  const d = new Date(iso);
  if (!iso || isNaN(d.getTime())) {
    return '';
  }
  try {
    const day = d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
    const time = d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${day}, ${time}`;
  } catch (e) {
    return String(iso).slice(0, 16).replace('T', ' ');
  }
};

const toCard = scan => {
  const status = STATUS_FROM_API[String(scan?.status).toLowerCase()];
  if (!status) {
    return null;
  }
  return {
    id: scan.id,
    status,
    // The scan has no station name of its own: use what the server sends,
    // else the station text read from the receipt, else the fuel.
    title:
      scan.stationName ||
      scan.station?.name ||
      scan.ocr?.stationText ||
      scan.fuelOption?.name ||
      'Receipt',
    date: formatDate(scan.receiptAt || scan.submittedAt || scan.createdAt),
    amount: scan.totalAmount != null ? Number(scan.totalAmount) : null,
    litres: scan.volume != null ? Number(scan.volume) : null,
    // The photo of the receipt that was uploaded
    receiptUrl: scan.receiptUrl || null,
    rejectReason: status === 'Rejected' ? scan.rejectReason : null,
  };
};

// Amber for pending from Stitch "Pompi - My Scans"; the theme has no
// amber-50/100/700 steps.
const amber = {
  wash: '#FFFBEB',
  line: '#FDE68A',
  text: '#B45309',
  icon: '#D97706',
};

const statusConfig = {
  Approved: {
    tileBg: colors.mintWash,
    tileBorder: colors.mintLine,
    iconColor: colors.primary,
    pillBg: colors.mintWash,
    pillBorder: colors.mintLine,
    textColor: colors.brandLink,
    cardBorder: colors.mintLine,
  },
  Pending: {
    tileBg: amber.wash,
    tileBorder: colors.warningContainer,
    iconColor: amber.icon,
    pillBg: amber.wash,
    pillBorder: amber.line,
    textColor: amber.text,
    cardBorder: null,
  },
  Rejected: {
    tileBg: colors.errorTint,
    tileBorder: colors.errorContainer,
    iconColor: colors.error,
    pillBg: colors.errorTint,
    pillBorder: colors.errorContainer,
    textColor: colors.onErrorContainer,
    cardBorder: null,
  },
};

const FILTERS = ['all', 'Approved', 'Pending', 'Rejected'];

const Glyph = ({children, color, size, strokeWidth = 1.8}) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round">
    {children}
  </Svg>
);

const ReceiptGlyph = ({color}) => (
  <Glyph color={color} size={24}>
    <Path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
    <Path d="M9 14l6-6" />
    <Circle cx={9.5} cy={8.5} r={0.5} />
    <Circle cx={14.5} cy={13.5} r={0.5} />
  </Glyph>
);

const CheckCircleGlyph = ({color}) => (
  <Glyph color={color} size={24}>
    <Circle cx={12} cy={12} r={9} />
    <Path d="M9 12l2 2 4-4" />
  </Glyph>
);

const XCircleGlyph = ({color}) => (
  <Glyph color={color} size={24}>
    <Circle cx={12} cy={12} r={9} />
    <Path d="M15 9l-6 6M9 9l6 6" />
  </Glyph>
);

const CalendarGlyph = () => (
  <Glyph color={colors.textSubtle} size={14} strokeWidth={2}>
    <Path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" />
  </Glyph>
);

const CheckGlyph = ({color}) => (
  <Glyph color={color} size={12} strokeWidth={3}>
    <Path d="M5 12l5 5L20 7" />
  </Glyph>
);

const statusIcons = {
  Approved: CheckCircleGlyph,
  Pending: ReceiptGlyph,
  Rejected: XCircleGlyph,
};

const formatEuro = value => `€${value.toFixed(2)}`;

const StatusPill = ({status, label}) => {
  const config = statusConfig[status];
  return (
    <View
      style={[
        styles.statusPill,
        {backgroundColor: config.pillBg, borderColor: config.pillBorder},
      ]}>
      {status === 'Approved' ? (
        <CheckGlyph color={colors.brandBright} />
      ) : (
        <View style={[styles.statusDot, {backgroundColor: config.iconColor}]} />
      )}
      <Text style={[styles.statusText, {color: config.textColor}]}>{label}</Text>
    </View>
  );
};

// Small outlined button that opens the receipt photo of a scan.
const ReceiptPill = ({label, onPress}) => (
  <Pressable
    onPress={onPress}
    hitSlop={6}
    accessibilityRole="button"
    accessibilityLabel={label}
    style={({pressed}) => [styles.receiptPill, pressed && styles.chipPressed]}>
    <Glyph color={colors.primary} size={12} strokeWidth={2.2}>
      <Path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
      <Path d="M9 8h6M9 12h6" />
    </Glyph>
    <Text style={styles.receiptPillText}>{label}</Text>
  </Pressable>
);

const ScanCard = ({item, statusLabel, receiptLabel, onOpenReceipt}) => {
  const config = statusConfig[item.status];
  const Icon = statusIcons[item.status];
  const hasAmount = item.amount != null;
  return (
    <View style={[styles.card, config.cardBorder && {borderColor: config.cardBorder}]}>
      <View
        style={[
          styles.iconTile,
          {backgroundColor: config.tileBg, borderColor: config.tileBorder},
        ]}>
        <Icon color={config.iconColor} />
      </View>
      <View style={styles.cardBody}>
        <Text style={styles.title} numberOfLines={2}>
          {item.title}
        </Text>
        <View style={styles.dateRow}>
          <CalendarGlyph />
          <Text style={styles.date}>{item.date}</Text>
        </View>
        {item.rejectReason ? (
          <Text style={styles.rejectReason} numberOfLines={2}>
            {item.rejectReason}
          </Text>
        ) : null}
        <View style={styles.footer}>
          {hasAmount ? (
            <View style={styles.amountRow}>
              <Text style={styles.amountMeta}>
                <Text style={styles.amount}>{formatEuro(item.amount)}</Text>
                {item.litres != null ? ` • ${item.litres.toFixed(1)} L` : ''}
              </Text>
              {item.savings != null ? (
                <View style={styles.savingsTag}>
                  <Text style={styles.savingsText}>
                    +{formatEuro(item.savings)}
                  </Text>
                </View>
              ) : null}
            </View>
          ) : (
            <View />
          )}
          <View style={styles.pills}>
            {item.receiptUrl ? (
              <ReceiptPill
                label={receiptLabel}
                onPress={() => onOpenReceipt(item.receiptUrl)}
              />
            ) : null}
            <StatusPill status={item.status} label={statusLabel} />
          </View>
        </View>
      </View>
    </View>
  );
};

const FilterChip = ({label, count, active, tone, onPress}) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityState={{selected: active}}
    style={({pressed}) => [
      styles.chip,
      active ? styles.chipActive : styles.chipIdle,
      pressed && styles.chipPressed,
    ]}>
    <Text style={[styles.chipText, active && styles.chipTextActive]}>
      {label}
    </Text>
    <View
      style={[
        styles.chipCount,
        active
          ? styles.chipCountActive
          : tone && {backgroundColor: tone.pillBg},
      ]}>
      <Text
        style={[
          styles.chipCountText,
          active
            ? styles.chipTextActive
            : tone && {color: tone.textColor},
        ]}>
        {count}
      </Text>
    </View>
  </Pressable>
);

const Scans = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const [filter, setFilter] = useState('all');
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Per-status totals from the server (user/rewards), so the chips stay
  // right when there are more scans than the one page loaded here.
  const [serverCounts, setServerCounts] = useState(null);
  // Url of the receipt photo that is open full screen (null = closed)
  const [receiptView, setReceiptView] = useState(null);
  const [receiptFailed, setReceiptFailed] = useState(false);
  const loadedOnce = useRef(false);

  // The scans come from GET scans/mine (the newest first, as the server sends).
  const loadScans = useCallback(async () => {
    const [result, rewards] = await Promise.all([
      getMyScans({limit: 50}),
      getRewards().catch(() => null),
    ]);
    if (rewards) {
      const {approved, pending, rejected} = rewards.scans;
      setServerCounts({
        all: approved + pending + rejected,
        Approved: approved,
        Pending: pending,
        Rejected: rejected,
      });
    }
    if (result.success) {
      setScans(result.scans.map(toCard).filter(Boolean));
      setLoadError(null);
    } else {
      setLoadError(result.error);
    }
    loadedOnce.current = true;
    setLoading(false);
    setRefreshing(false);
  }, []);

  // Every time the screen is opened, so a receipt just sent shows up.
  useFocusEffect(
    useCallback(() => {
      if (!loadedOnce.current) {
        setLoading(true);
      }
      loadScans();
    }, [loadScans]),
  );

  // ProfileList opens this screen with {from: 'ProfileList'}.
  // Only then do we show a back arrow that returns to the Profile screen.
  const fromProfile = route.params?.from === 'ProfileList';

  // Forget the flag when leaving, so opening Scans later from somewhere else
  // (e.g. the tab bar) doesn't show the back arrow.
  useFocusEffect(
    useCallback(
      () => () => navigation.setParams({from: undefined}),
      [navigation],
    ),
  );

  const handleBack = () => {
    // tab 'ProfileTab' -> its first screen 'ProfileList'
    navigation.navigate('ProfileTab', {screen: 'ProfileList'});
  };

  const counts = useMemo(() => {
    if (serverCounts) {
      return serverCounts;
    }
    const result = {all: scans.length};
    scans.forEach(item => {
      result[item.status] = (result[item.status] || 0) + 1;
    });
    return result;
  }, [scans, serverCounts]);

  const visible = useMemo(
    () =>
      filter === 'all' ? scans : scans.filter(item => item.status === filter),
    [filter, scans],
  );

  return (
    <View style={styles.screen}>
      <AuthHeader
        title={t('root.MyScans.title')}
        onBack={fromProfile ? handleBack : undefined}
      />
      <View style={styles.filters}>
        {FILTERS.map(key => (
          <FilterChip
            key={key}
            label={t(`root.MyScans.${key}`, {defaultValue: key === 'all' ? 'All' : key})}
            count={counts[key] || 0}
            active={filter === key}
            tone={statusConfig[key]}
            onPress={() => setFilter(key)}
          />
        ))}
      </View>
      <FlatList
        data={visible}
        keyExtractor={item => String(item.id)}
        renderItem={({item}) => (
          <ScanCard
            item={item}
            receiptLabel={t('root.MyScans.receipt', {defaultValue: 'Receipt'})}
            onOpenReceipt={setReceiptView}
            statusLabel={t(`root.MyScans.${item.status}`, {
              defaultValue: item.status,
            })}
          />
        )}
        refreshing={refreshing}
        onRefresh={() => {
          setRefreshing(true);
          loadScans();
        }}
        contentContainerStyle={styles.list}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          loading ? (
            <ActivityIndicator
              style={styles.empty}
              color={colors.primary}
            />
          ) : loadError ? (
            <Text style={styles.empty}>{loadError}</Text>
          ) : (
            <Text style={styles.empty}>{t('root.MyScans.empty')}</Text>
          )
        }
      />
      <Modal
        visible={!!receiptView}
        transparent
        animationType="fade"
        onRequestClose={() => setReceiptView(null)}>
        <Pressable
          style={styles.viewerBackdrop}
          onPress={() => {
            setReceiptView(null);
            setReceiptFailed(false);
          }}>
          {receiptFailed ? (
            <Text style={styles.viewerText}>
              {t('root.MyScans.receiptError', {
                defaultValue: 'The receipt could not be opened.',
              })}
            </Text>
          ) : receiptView ? (
            <Image
              source={{uri: receiptView}}
              style={styles.viewerImage}
              onError={() => setReceiptFailed(true)}
            />
          ) : null}
          <Text style={styles.viewerText}>
            {t('root.MyScans.close', {defaultValue: 'Tap to close'})}
          </Text>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  pills: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  receiptPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  receiptPillText: {
    fontFamily: fonts.semiBold,
    fontSize: 11,
    lineHeight: 14,
    color: colors.primary,
  },
  viewerBackdrop: {
    flex: 1,
    backgroundColor: colors.overlay,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    gap: spacing.md,
  },
  viewerImage: {
    width: '100%',
    height: '85%',
    resizeMode: 'contain',
  },
  viewerText: {
    ...typography.labelLg,
    color: colors.onPrimary,
  },
  rejectReason: {
    ...typography.bodySm,
    color: colors.onErrorContainer,
    marginBottom: spacing.xs,
  },
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },

  // Filters
  filters: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.margin,
    paddingBottom: spacing.xs,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: spacing.margin,
    paddingVertical: 6,
    borderRadius: radii.pill,
    borderWidth: 1,
  },
  chipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  chipIdle: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
  },
  chipPressed: {
    transform: [{scale: 0.97}],
  },
  chipText: {
    ...typography.labelMd,
    letterSpacing: 0,
    color: '#475569',
  },
  chipTextActive: {
    color: colors.onPrimary,
  },
  chipCount: {
    minWidth: 18,
    paddingHorizontal: 6,
    borderRadius: radii.pill,
    alignItems: 'center',
    backgroundColor: colors.surfaceMuted,
  },
  chipCountActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
  },
  chipCountText: {
    fontFamily: fonts.bold,
    fontSize: 10,
    lineHeight: 16,
    color: colors.textMuted,
  },

  // List
  list: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.xxl,
    gap: 14,
  },
  card: {
    ...card,
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: spacing.margin,
    gap: 14,
  },
  iconTile: {
    width: 44,
    height: 44,
    borderRadius: radii.sm,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontFamily: fonts.bold,
    fontSize: 13.5,
    lineHeight: 19,
    color: colors.text,
    textTransform: 'uppercase',
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: spacing.xs,
  },
  date: {
    ...typography.bodySm,
    color: colors.textSubtle,
    flexShrink: 1,
  },
  footer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginTop: spacing.md,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: colors.borderSubtle,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
  },
  amountMeta: {
    fontFamily: fonts.medium,
    fontSize: 12,
    lineHeight: 16,
    color: '#475569',
  },
  amount: {
    fontFamily: fonts.bold,
    color: colors.text,
  },
  savingsTag: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: colors.mintWash,
  },
  savingsText: {
    fontFamily: fonts.bold,
    fontSize: 10,
    lineHeight: 14,
    color: colors.secondary,
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: radii.pill,
    borderWidth: 1,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontFamily: fonts.semiBold,
    fontSize: 11,
    lineHeight: 14,
  },
  empty: {
    ...typography.bodyMd,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: spacing.xxl,
  },
});

export default Scans;